<script setup lang="ts">
import {
  compactXp,
  hexAlpha,
  mixColor,
  roundProgressPercent,
} from '@/utils/levels';

// Cartão de nível do Clube Fidelidade (desktop e mobile): o elo do jogador
// vestindo o TierSkin (mesma pele do vip/levels), com nível à esquerda,
// distância pro próximo à direita, emblema com o avatar + nome + "Clique e
// confira" no meio e a barra de XP fechando. Clicar avisa o hub, que fecha e
// navega pra tela de níveis.
const {
  tier,
  level,
  isMaxLevel,
  levelProgress,
  xpToNextLevel,
  currentLevelLabel,
  nextLevelLabel,
  playerName,
  playerAvatar,
  openChangeNickname,
} = useLevelsProgress();

const emit = defineEmits<{ select: [] }>();

/** Segunda linha: no VIP o nome do elo já é o título, então mostra o nível. */
const tierLine = computed(() =>
  tier.value.isVip ? `Nível ${level.value}` : tier.value.name,
);

const gapLine = computed(() =>
  isMaxLevel.value
    ? 'Topo da escala'
    : `Faltam ${compactXp(xpToNextLevel.value)} XP`,
);

/** "Para o nível 12" em caixa baixa, mas "Para o VIP 3" preserva a sigla. */
const progressLabel = computed(() => {
  if (isMaxLevel.value) return 'Nível máximo alcançado';

  const label = nextLevelLabel.value;

  return `Para o ${label.startsWith('VIP') ? label : label.toLowerCase()}`;
});
</script>

<template>
  <button
    aria-label="Ver seu progresso de nível"
    class="block w-full text-left"
    type="button"
    @click="emit('select')"
  >
    <LevelsTierSkin
      :radius="14"
      :tier="tier"
    >
      <div
        class="relative flex flex-col gap-2 px-4 pb-3 pt-2.5 lg:px-5 lg:pb-3.5"
      >
        <!-- Emblema com o avatar, GRANDE e flutuando metade pra fora do topo
             do cartão (invade a arte do header, como no desenho) -->
        <span
          aria-hidden="true"
          class="pointer-events-none absolute left-1/2 top-[-48px] z-[2] block h-[94px] w-[94px] -translate-x-1/2 lg:top-[-60px] lg:h-[118px] lg:w-[118px]"
        >
          <span
            class="absolute inset-0 bg-contain bg-center bg-no-repeat"
            :style="{
              backgroundImage: `url(${tier.badge})`,
              filter: 'drop-shadow(0 6px 16px rgba(0,0,0,.6))',
            }"
          ></span>
          <span
            class="absolute left-[26%] top-[32%] h-[48%] w-[48%] rounded-full p-[2.5px]"
            :style="{
              background: `linear-gradient(140deg,${tier.color},${mixColor(tier.color, '#ffffff', 0.38)})`,
              boxShadow: `0 0 16px ${hexAlpha(tier.color, 0.3)},0 5px 14px rgba(0,0,0,.45)`,
            }"
          >
            <span
              class="block h-full w-full rounded-full"
              :style="{
                backgroundImage: `url(${playerAvatar}),radial-gradient(circle at 50% 34%,${mixColor(tier.color, '#050C22', 0.1)} 0%,${mixColor(
                  tier.color,
                  '#050C22',
                  0.48,
                )} 46%,#050A18 100%)`,
                backgroundSize: '88% auto,cover',
                backgroundRepeat: 'no-repeat,no-repeat',
                backgroundPosition: 'center bottom,center',
              }"
            ></span>
          </span>
        </span>

        <div class="flex items-start justify-between gap-3">
          <div class="flex min-w-0 flex-col gap-px">
            <span
              class="truncate text-[14px] font-bold leading-[18px] tracking-[-0.02em] text-white lg:text-[15px]"
            >
              {{ currentLevelLabel }}
            </span>
            <span
              class="truncate text-[11px] font-medium leading-[15px] text-white/[0.72]"
            >
              {{ tierLine }}
            </span>
          </div>

          <div class="flex min-w-0 flex-col items-end gap-px text-right">
            <span
              class="truncate text-[14px] font-bold leading-[18px] tracking-[-0.02em] text-white lg:text-[15px]"
            >
              {{ isMaxLevel ? 'Nível máximo' : 'Próximo nível' }}
            </span>
            <span
              class="truncate text-[11px] font-medium leading-[15px] text-white/[0.78]"
            >
              {{ gapLine }}
            </span>
          </div>
        </div>

        <!-- Nome + chamada centrados logo abaixo do emblema flutuante -->
        <div class="flex items-center justify-center gap-2.5 pt-3">
          <span
            class="truncate text-[15px] font-extrabold leading-5 tracking-[-0.02em] text-white"
          >
            {{ playerName }}
          </span>

          <!-- O cartão inteiro é um <button> que navega, então o lápis não pode
               ser outro botão (HTML inválido) nem deixar o clique subir: é um
               span com papel de botão e `.stop` nos dois gatilhos. -->
          <span
            aria-label="Editar apelido"
            class="flex-none cursor-pointer text-[13px] leading-none text-white/70 hover:text-white"
            role="button"
            tabindex="0"
            @click.stop="openChangeNickname"
            @keydown.enter.stop.prevent="openChangeNickname"
            @keydown.space.stop.prevent="openChangeNickname"
          >
            <NuxtIcon name="pen_square" />
          </span>

          <span
            class="flex-none whitespace-nowrap rounded-full bg-[rgba(0,6,20,0.42)] px-2.5 py-1 text-[9.5px] font-bold uppercase leading-3 tracking-[0.06em] text-white/[0.85] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.16)]"
          >
            Clique e confira
          </span>
        </div>

        <div class="flex flex-col gap-1.5">
          <div class="flex items-baseline justify-between gap-3">
            <span
              class="truncate text-[11px] font-medium leading-[15px] text-white/[0.72]"
            >
              {{ progressLabel }}
            </span>
            <span
              class="flex-none text-[14px] font-extrabold leading-[18px] text-white tabular-nums"
            >
              {{ roundProgressPercent(levelProgress) }}%
            </span>
          </div>
          <div
            class="h-[7px] overflow-hidden rounded-full bg-[rgba(0,5,20,0.6)] shadow-[inset_0_1px_2px_rgba(0,0,0,0.5)]"
          >
            <div
              class="h-full rounded-full"
              :style="{
                width: `${levelProgress}%`,
                background: `linear-gradient(90deg,${tier.color},${mixColor(tier.color, '#ffffff', 0.42)})`,
                boxShadow: `0 0 10px ${hexAlpha(tier.color, 0.5)}`,
              }"
            ></div>
          </div>
        </div>
      </div>
    </LevelsTierSkin>
  </button>
</template>
